import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBrands } from '../contexts/BrandContext'
import { Forbidden } from '../components/common/Forbidden'
import { Icons } from '../components/icons'
import { can } from '../utils/permissions'
import { avatarGradient, brandColor, brandInitial } from '../utils/brand'
import { initials } from '../utils/text'
import { notifySuccess, notifyError } from '../utils/notify'
import { loadAccounts, createAccount, resendInvite } from '../services/accountStore'
import type { Account } from '../types'
import { Tabs, TabsList, TabsTrigger } from '@/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'

type LoadStatus = 'loading' | 'ready'
type Tab = 'users' | 'admins'
type AdminRole = 'admin' | 'brand-admin'

interface AdminFormState {
  email: string
  role: AdminRole
  brandKey: string
  displayName: string
}

const EMPTY_FORM: AdminFormState = { email: '', role: 'brand-admin', brandKey: '', displayName: '' }
const ACCOUNTS_ROUTE = '/admin/accounts'

/** True, wenn der Fehler einen HTTP-409-Status (Conflict, doppelte E-Mail) trägt. */
function isConflict(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('response' in err)) return false
  return (err as { response?: { status?: number } }).response?.status === 409
}

/** Nutzerverwaltung für Admin/Brand-Admin (Spec §7.3): Tabs vCard-Nutzer / Admins.
 *  BMC-Designsystem `console.css`. */
export function AccountListPage() {
  const { session } = useAuth()
  const { brands, resolveBrand } = useBrands()
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [tab, setTab] = useState<Tab>('users')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<AdminFormState>(EMPTY_FORM)
  const [creating, setCreating] = useState(false)
  const [invitingId, setInvitingId] = useState<string | null>(null)

  const allowed = session ? can(session).accountsView() : false

  const reload = useCallback(() => {
    if (!session) return Promise.resolve()
    return loadAccounts(session).then(setAccounts)
  }, [session])

  useEffect(() => {
    if (!session || !allowed) return
    let cancelled = false
    setStatus('loading')
    loadAccounts(session).then((list) => {
      if (cancelled) return
      setAccounts(list)
      setStatus('ready')
    })
    return () => {
      cancelled = true
    }
  }, [session, allowed])

  const handleCreateSubmit = useCallback(async () => {
    setCreating(true)
    try {
      await createAccount({
        email: form.email,
        role: form.role,
        brandKey: form.role === 'brand-admin' ? form.brandKey || undefined : undefined,
        displayName: form.displayName || undefined,
      })
      notifySuccess('Admin eingeladen — Einladungs-Mail versendet')
      setShowForm(false)
      setForm(EMPTY_FORM)
      await reload()
    } catch (err) {
      notifyError(isConflict(err) ? 'E-Mail bereits vergeben.' : 'Anlegen fehlgeschlagen, bitte erneut.')
    } finally {
      setCreating(false)
    }
  }, [form, reload])

  const handleResendInvite = useCallback(async (id: string) => {
    setInvitingId(id)
    try {
      await resendInvite(id)
      notifySuccess('Einladung erneut gesendet')
    } catch {
      notifyError('Einladung konnte nicht gesendet werden, bitte erneut.')
    } finally {
      setInvitingId(null)
    }
  }, [])

  if (!session) return null

  if (!allowed) return <Forbidden message="Kein Zugriff auf die Nutzerverwaltung." />

  const userAccounts = accounts.filter((a) => a.role === 'user')
  const adminAccounts = accounts.filter((a) => a.role === 'admin' || a.role === 'brand-admin')
  const visibleAccounts = tab === 'users' ? userAccounts : adminAccounts

  return (
    <>
      <section className="cover">
        <div className="eyebrow">Nutzerverwaltung</div>
        <h1>
          <span className="hl">Nutzer</span> verwalten
        </h1>
        <p>Nutzerkonten entstehen automatisch mit der vCard — hier siehst du alle Konten in deinem Bereich.</p>
      </section>

      <Tabs value={tab} onValueChange={(v: string) => setTab(v as Tab)} className="mb-4">
        <TabsList>
          <TabsTrigger value="users">vCard-Nutzer</TabsTrigger>
          <TabsTrigger value="admins">Admins</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'users' && (
        <div role="note" className="ctxbar">
          {Icons.lock}
          <span>
            Nutzerkonten entstehen automatisch mit der vCard (E-Mail = Login).{' '}
            {session.role === 'brand-admin'
              ? 'Als Brand-Admin bearbeitest du vCards deiner Brand — anlegen tut der Admin.'
              : 'Ein neuer Nutzer entsteht, sobald du eine vCard anlegst.'}
          </span>
        </div>
      )}

      {tab === 'admins' && showForm && (
        <section className="panel">
          <div className="head">
            {Icons.plus}
            <h2>Neuer Admin</h2>
          </div>
          <div className="pad">
            <div role="note" className="ctxbar mb-4">
              {Icons.mail}
              <span>
                Kein Passwort nötig: Das Konto wird passwortlos angelegt und der neue Admin erhält eine
                Einladungs-Mail, um sein Passwort selbst festzulegen (Link 7 Tage gültig).
              </span>
            </div>
            <div className="flex flex-col gap-4">
              <div className="field">
                <label className="flabel" htmlFor="f_newAdminEmail">
                  E-Mail
                </label>
                <input
                  className="inp"
                  id="f_newAdminEmail"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="field">
                <label className="flabel" htmlFor="f_newAdminDisplayName">
                  Anzeigename <span className="opt">(optional)</span>
                </label>
                <input
                  className="inp"
                  id="f_newAdminDisplayName"
                  type="text"
                  value={form.displayName}
                  onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                />
              </div>
              <div className="field">
                <label className="flabel" htmlFor="f_newAdminRole">
                  Rolle
                </label>
                <Select value={form.role} onValueChange={(v: string) => setForm((f) => ({ ...f, role: v as AdminRole }))}>
                  <SelectTrigger id="f_newAdminRole" aria-label="Rolle" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="brand-admin">Brand-Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === 'brand-admin' && (
                <div className="field">
                  <label className="flabel" htmlFor="f_newAdminBrand">
                    Brand
                  </label>
                  <Select value={form.brandKey} onValueChange={(v: string) => setForm((f) => ({ ...f, brandKey: v }))}>
                    <SelectTrigger id="f_newAdminBrand" aria-label="Brand" className="w-full">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map((b) => (
                        <SelectItem key={b.brandKey} value={b.brandKey}>
                          {b.brandName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-end gap-2.5">
              <button type="button" className="btn outline" onClick={() => setShowForm(false)}>
                Abbrechen
              </button>
              <button type="button" className="btn primary" disabled={creating} onClick={handleCreateSubmit}>
                {Icons.mail} Einladen
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="panel">
        <div className="head">
          <h2>{tab === 'users' ? 'Nutzer' : 'Admins'}</h2>
          <span className="sub">{visibleAccounts.length}</span>
          <span className="spacer" />
          {tab === 'admins' && session.role === 'admin' && (
            <button type="button" className="btn primary sm" onClick={() => setShowForm((v) => !v)}>
              {Icons.plus} Admin hinzufügen
            </button>
          )}
        </div>

        <div className="list">
          {status === 'loading' ? (
            <div className="empty">{tab === 'users' ? 'Nutzer werden geladen …' : 'Admins werden geladen …'}</div>
          ) : visibleAccounts.length === 0 ? (
            <div className="empty">
              {tab === 'users' ? 'Keine Nutzer in deinem Bereich.' : 'Keine Admins in deinem Bereich.'}
            </div>
          ) : (
            visibleAccounts.map((a) => {
              const brand = a.brandKey ? resolveBrand(a.brandKey) : null
              return (
                <div className="lrow" key={a.id}>
                  <span className="pic" style={{ background: avatarGradient(a.id) }}>
                    {initials(a.displayName)}
                  </span>
                  <div className="who">
                    <div className="nm">{a.displayName}</div>
                    <div className="mt">{a.email}</div>
                  </div>
                  <div className="side">
                    {brand && (
                      <span className="chip-brand" style={{ background: brandColor(brand) }} title={brand.brandName}>
                        {brandInitial(brand)}
                      </span>
                    )}
                    <span className="badge role">{a.role}</span>
                    {tab === 'admins' && a.hasPassword === false ? (
                      <span className="badge inactive" title="Konto noch ohne Passwort">
                        Einladung ausstehend
                      </span>
                    ) : (
                      <span className={`badge ${a.active ? 'active' : 'inactive'}`}>
                        {a.active && <span className="dot" />}
                        {a.active ? 'aktiv' : 'inaktiv'}
                      </span>
                    )}
                    {tab === 'admins' && session.role === 'admin' && a.hasPassword === false && (
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={invitingId === a.id}
                        onClick={() => handleResendInvite(a.id)}
                      >
                        {Icons.mail} Einladung erneut senden
                      </button>
                    )}
                    {tab === 'admins' && (
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => navigate(`${ACCOUNTS_ROUTE}/${a.id}`)}
                      >
                        {Icons.edit} Bearbeiten
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </section>
    </>
  )
}
